import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material';
import {
  Favorite,
  FlightTakeoff,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { signOut } from 'firebase/auth';
import { auth, completeProfile, createUser, resetPassword, signInUser, signInWithGoogle } from '../firebase';
import logo from "../imgs/wandr-mark.svg";
import PlaceAutocomplete, { COUNTRIES, loadStates, searchLocalities } from "./PlaceAutocomplete";
import "./login.css";

const APP_NAME = "Wandr";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const VIEWS = {
  login: {
    title: 'Bienvenido',
    subtitle: `Ingresa a tu cuenta de ${APP_NAME}`,
    submit: 'Entrar',
  },
  register: {
    title: 'Crea tu cuenta',
    subtitle: `Únete a ${APP_NAME} y empieza a viajar`,
    submit: 'Registrarse',
  },
  forgot: {
    title: 'Recuperar contraseña',
    subtitle: 'Te enviaremos un enlace para restablecerla',
    submit: 'Enviar enlace',
  },
  // After signing in with Google for the first time (App routes here until the profile is complete).
  complete: {
    title: 'Completá tu perfil',
    subtitle: 'Necesitamos algunos datos más para terminar tu registro',
    submit: 'Continuar',
  },
};

// Popup closed or replaced by another one: not an error worth showing.
const GOOGLE_CANCELLED = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const inputSx = {
  height: 40,
  borderRadius: '10px',
  fontSize: '0.85rem',
  bgcolor: 'rgba(22, 32, 34, 0.9)',
  transition: 'box-shadow 0.25s ease, background-color 0.25s ease',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(94, 234, 212, 0.1)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(94, 234, 212, 0.3)',
  },
  '&.Mui-focused': {
    bgcolor: 'rgba(22, 36, 38, 1)',
    boxShadow: '0 0 0 3px rgba(45, 212, 191, 0.15)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2dd4bf',
    borderWidth: 1,
  },
  // Fill the 40px field exactly, so the autofill cover in login.css doesn't spill outside it.
  '& .MuiOutlinedInput-input': {
    py: 0,
    height: '100%',
  },
  '& input::placeholder': {
    color: 'rgba(235, 255, 244, 0.45)',
    opacity: 1,
  },
};

const linkSx = {
  color: '#2dd4bf',
  fontSize: '0.75rem',
  textDecoration: 'none',
  verticalAlign: 'baseline',
  '&:hover': { textDecoration: 'underline' }
};

const mutedTextSx = { fontSize: '0.75rem', color: 'rgba(235, 255, 244, 0.55)' };

const SceneCard = ({ className, badge }) => (
  <div className={`scene-card ${className}`}>
    <div className="scene-media">
      {className === 'scene-card--front' && <FlightTakeoff className="plane" />}
    </div>
    <div className="scene-body">
      <div className="scene-row">
        <span className="scene-avatar" />
        <span className="scene-line" style={{ width: '60%', marginBottom: 0 }} />
      </div>
      <div className="scene-line" style={{ width: '85%' }} />
      <div className="scene-line" style={{ width: '65%' }} />
    </div>
    {badge}
  </div>
);

// The province is a { name, lat, lon } option, or plain text when typed by hand.
const stateNameOf = (state) => (typeof state === 'string' ? state : state?.name || '');

const initialForm = {
  email: '',
  password: '',
  confirmPassword: '',
  displayName: '',
  phone: '',
  country: null,
  state: '',
  locality: '',
};

const Login = ({ initialView = 'login' }) => {
  const navigate = useNavigate();

  const [view, setView] = useState(initialView);
  const [hasSwitched, setHasSwitched] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Completing a Google sign-up: start from what Google already knows.
  useEffect(() => {
    if (view !== 'complete' || !auth.currentUser) return;
    const { email, displayName } = auth.currentUser;
    setFormData((prev) => ({ ...prev, email: email || '', displayName: prev.displayName || displayName || '' }));
  }, [view]);

  const particles = useMemo(
    () => Array.from({ length: 18 }, () => ({
      left: `${Math.random() * 100}%`,
      animationDuration: `${10 + Math.random() * 12}s`,
      animationDelay: `${Math.random() * 10}s`,
    })),
    []
  );

  // On first load the fields wait for the card to enter; after a view switch they animate right away.
  const delay = (i) => ({ animationDelay: `${(hasSwitched ? 0.05 : 0.45) + i * 0.06}s` });

  const goTo = (nextView) => {
    setView(nextView);
    setHasSwitched(true);
    setStatus({ type: '', message: '' });
    setShowPassword(false);
    setFormData((prev) => ({ ...initialForm, email: prev.email }));
  };

  const updateForm = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const handleChange = (prop) => (event) => updateForm({ [prop]: event.target.value });

  const countryCode = formData.country?.code;
  const [states, setStates] = useState({ countryCode: null, list: [] });

  useEffect(() => {
    if (!countryCode) return undefined;
    let cancelled = false;
    loadStates(countryCode)
      .then((list) => !cancelled && setStates({ countryCode, list }))
      .catch(() => !cancelled && setStates({ countryCode, list: [] }));
    return () => { cancelled = true; };
  }, [countryCode]);

  const statesLoading = Boolean(countryCode) && states.countryCode !== countryCode;

  const searchLocalityInState = useCallback(
    (query, signal) => searchLocalities(
      query,
      countryCode,
      typeof formData.state === 'string' ? { name: formData.state } : formData.state,
      signal
    ),
    [countryCode, formData.state]
  );

  const fail = (message) => {
    setStatus({ type: 'error', message });
    return false;
  };

  const validateForm = () => {
    const { email, password, confirmPassword, displayName, phone, country, state, locality } = formData;

    if (view !== 'complete' && !email.trim()) return fail('Por favor ingrese su email.');
    if (view !== 'complete' && !EMAIL_REGEX.test(email)) return fail('Por favor ingrese un email válido.');
    if (view === 'forgot') return true;

    if (view !== 'complete' && !password) return fail('Por favor ingrese su contraseña.');
    if (view === 'login') return true;

    if ((view === 'register' && !confirmPassword) || !displayName.trim() || !phone.trim()) {
      return fail('Por favor complete todos los campos.');
    }
    if (!country) return fail('Elegí tu país de la lista.');
    if (!stateNameOf(state).trim()) return fail('Ingresá tu provincia.');
    if (!locality.trim()) return fail('Ingresá tu localidad.');
    if (view === 'register' && password !== confirmPassword) return fail('Las contraseñas no coinciden.');

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setStatus({ type: '', message: '' });
    try {
      if (view === 'login') {
        await signInUser(formData.email, formData.password);
        navigate('/home');
      } else if (view === 'register') {
        const { country, state, locality } = formData;
        await createUser(
          formData.email,
          formData.password,
          formData.displayName,
          formData.phone,
          [locality, stateNameOf(state), country.name].join(', '),
          { locality, state: stateNameOf(state), country: country.name, countryCode: country.code }
        );
        navigate('/home');
      } else if (view === 'complete') {
        const { country, state, locality } = formData;
        await completeProfile({
          displayName: formData.displayName.trim(),
          phone: formData.phone.trim(),
          point: [locality, stateNameOf(state), country.name].join(', '),
          location: { locality, state: stateNameOf(state), country: country.name, countryCode: country.code },
        });
        // App leaves /complete-profile for /home as soon as it sees the saved profile.
      } else {
        await resetPassword(formData.email);
        setStatus({
          type: 'success',
          message: 'Te enviamos un email para restablecer tu contraseña. Revisa Spam y Promociones.',
        });
      }
    } catch (error) {
      console.error(error.message);
      setStatus({
        type: 'error',
        message: error.message || 'Ocurrió un error. Intenta nuevamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // App takes it from here: /home with a complete profile, /complete-profile otherwise.
  const handleGoogle = async () => {
    setIsLoading(true);
    setStatus({ type: '', message: '' });
    try {
      await signInWithGoogle();
    } catch (error) {
      if (!GOOGLE_CANCELLED.includes(error.code)) {
        console.error(error);
        setStatus({ type: 'error', message: 'No pudimos iniciar sesión con Google. Intenta nuevamente.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const switchAccount = async () => {
    setIsLoading(true);
    await signOut(auth).catch(() => {});
    setIsLoading(false);
  };

  const isProfileForm = view === 'register' || view === 'complete';

  const passwordToggle = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPassword(!showPassword)}
        edge="end"
        size="small"
        disabled={isLoading}
        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        sx={{ color: 'rgba(235, 255, 244, 0.45)' }}
      >
        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const fields = [
    {
      name: 'email',
      placeholder: 'Email',
      type: 'email',
      autoComplete: 'email',
      // The Google account's email, shown for reference.
      readOnly: view === 'complete',
    },
    (view === 'login' || view === 'register') && {
      name: 'password',
      placeholder: 'Contraseña',
      type: showPassword ? 'text' : 'password',
      autoComplete: view === 'login' ? 'current-password' : 'new-password',
      endAdornment: passwordToggle,
    },
    view === 'register' && {
      name: 'confirmPassword',
      placeholder: 'Repetir contraseña',
      type: showPassword ? 'text' : 'password',
      autoComplete: 'new-password',
    },
    isProfileForm && { name: 'displayName', placeholder: 'Nombre', autoComplete: 'name' },
    isProfileForm && { name: 'phone', placeholder: 'Teléfono', type: 'tel', autoComplete: 'tel' },
    isProfileForm && {
      name: 'country',
      placeholder: 'País',
      render: (placeholder) => (
        <PlaceAutocomplete
          value={formData.country}
          options={COUNTRIES}
          getOptionLabel={(country) => country.name}
          onChange={(country) => updateForm({ country, state: '', locality: '' })}
          disabled={isLoading}
          placeholder={placeholder}
          inputSx={inputSx}
        />
      ),
    },
    isProfileForm && {
      name: 'state',
      placeholder: 'Provincia',
      render: (placeholder) => (
        <PlaceAutocomplete
          value={formData.state}
          options={states.list}
          getOptionLabel={stateNameOf}
          // Countries without provinces in the dataset get a free-text field.
          freeSolo={!statesLoading && states.list.length === 0}
          loading={statesLoading}
          onChange={(state) => updateForm({ state: state || '', locality: '' })}
          disabled={isLoading || !formData.country}
          placeholder={placeholder}
          inputSx={inputSx}
        />
      ),
    },
    isProfileForm && {
      name: 'locality',
      placeholder: 'Localidad',
      render: (placeholder) => (
        <PlaceAutocomplete
          value={formData.locality}
          search={searchLocalityInState}
          onChange={(locality) => updateForm({ locality: locality || '' })}
          disabled={isLoading || !formData.state}
          placeholder={placeholder}
          inputSx={inputSx}
        />
      ),
    },
  ].filter(Boolean);

  const { title, subtitle, submit } = VIEWS[view];

  return (
    <div className="login-page">
      <div className="login-bg-glow" />
      <div className="login-particles">
        {particles.map((style, i) => <span key={i} style={style} />)}
      </div>

      <div className="login-shell">
        <section className="login-hero">
          <div className="login-scene" aria-hidden="true">
            <SceneCard className="scene-card--back-left" />
            <SceneCard
              className="scene-card--back-right"
              badge={<span className="scene-badge scene-badge--heart"><Favorite /></span>}
            />
            <SceneCard
              className="scene-card--front"
              badge={<span className="scene-badge scene-badge--plane"><FlightTakeoff /></span>}
            />
          </div>

          <h2 className="login-headline">
            Descubrí los viajes que <span className="accent">te esperan.</span>
          </h2>
          <p className="login-tagline">{APP_NAME} es tu próxima aventura.</p>
        </section>

        <section className="login-card">
          <div className="login-logo">
            <img src={logo} alt={APP_NAME} />
          </div>

          <div key={view} className={hasSwitched ? 'auth-view' : undefined}>
            <h1 className="login-title">{title}</h1>
            <p className="login-subtitle">{subtitle}</p>

            {status.message && (
              <Alert
                severity={status.type === 'success' ? 'success' : 'error'}
                sx={{ mb: 2, borderRadius: '10px', fontSize: '0.8rem' }}
              >
                {status.message}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={1.25}>
                {fields.map(({ name, placeholder, type = 'text', autoComplete, endAdornment, render, readOnly }, i) => (
                  <div key={name} className="login-field" style={delay(i)}>
                    {render ? render(placeholder) : (
                    <OutlinedInput
                      id={name}
                      type={type}
                      fullWidth
                      placeholder={placeholder}
                      autoComplete={autoComplete}
                      inputProps={{ 'aria-label': placeholder }}
                      value={formData[name]}
                      onChange={handleChange(name)}
                      disabled={isLoading || readOnly}
                      endAdornment={endAdornment}
                      sx={inputSx}
                    />
                    )}
                  </div>
                ))}

                <div className="login-field" style={delay(fields.length)}>
                  <Button
                    type="submit"
                    fullWidth
                    disabled={isLoading}
                    className="login-submit"
                    sx={{
                      height: 38,
                      borderRadius: '10px',
                      fontSize: '0.85rem',
                      color: '#04170d',
                      backgroundImage: 'linear-gradient(90deg, #2dd4bf 0%, #22c55e 100%)',
                      boxShadow: '0 8px 24px rgba(34, 197, 94, 0.35)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 12px 30px rgba(45, 212, 191, 0.5)',
                      },
                      '&:active': { transform: 'translateY(0)' },
                      '&.Mui-disabled': { color: '#04170d', opacity: 0.7 },
                    }}
                  >
                    {isLoading ? <CircularProgress size={20} color="inherit" /> : submit}
                  </Button>
                </div>

                {(view === 'login' || view === 'register') && (
                  <div className="login-field" style={delay(fields.length)}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1.25,
                        '&::before, &::after': {
                          content: '""',
                          flex: 1,
                          height: '1px',
                          bgcolor: 'rgba(94, 234, 212, 0.18)',
                        },
                      }}
                    >
                      <Typography sx={mutedTextSx}>o</Typography>
                    </Box>
                    <Button
                      type="button"
                      fullWidth
                      onClick={handleGoogle}
                      disabled={isLoading}
                      startIcon={<GoogleLogo />}
                      sx={{
                        height: 38,
                        borderRadius: '10px',
                        fontSize: '0.85rem',
                        color: 'rgba(235, 255, 244, 0.9)',
                        bgcolor: 'rgba(22, 32, 34, 0.9)',
                        border: '1px solid rgba(94, 234, 212, 0.2)',
                        '&:hover': { bgcolor: 'rgba(22, 36, 38, 1)', borderColor: 'rgba(94, 234, 212, 0.45)' },
                      }}
                    >
                      Continuar con Google
                    </Button>
                  </div>
                )}
              </Stack>

              <div className="login-field" style={delay(fields.length + 1)}>
                <Stack spacing={0.5} alignItems="center" sx={{ mt: 2 }}>
                  {view === 'login' && (
                    <>
                      <Link component="button" type="button" onClick={() => goTo('forgot')} disabled={isLoading} sx={linkSx}>
                        ¿Olvidaste tu contraseña?
                      </Link>
                      <Typography sx={mutedTextSx}>
                        ¿No tienes cuenta?{' '}
                        <Link component="button" type="button" onClick={() => goTo('register')} disabled={isLoading} sx={linkSx}>
                          Regístrate
                        </Link>
                      </Typography>
                    </>
                  )}

                  {view === 'register' && (
                    <Typography sx={mutedTextSx}>
                      ¿Ya tienes cuenta?{' '}
                      <Link component="button" type="button" onClick={() => goTo('login')} disabled={isLoading} sx={linkSx}>
                        Inicia sesión
                      </Link>
                    </Typography>
                  )}

                  {view === 'complete' && (
                    <Link component="button" type="button" onClick={switchAccount} disabled={isLoading} sx={linkSx}>
                      Usar otra cuenta
                    </Link>
                  )}

                  {view === 'forgot' && (
                    <>
                      <Typography sx={{ ...mutedTextSx, textAlign: 'center' }}>
                        Si no llega en 2 a 3 minutos, revisa Spam/Promociones.
                      </Typography>
                      <Link component="button" type="button" onClick={() => goTo('login')} disabled={isLoading} sx={linkSx}>
                        Volver a iniciar sesión
                      </Link>
                    </>
                  )}
                </Stack>
              </div>
            </Box>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
